// Looping "peek" carousel: the active slide stays centered with neighbors visible on both sides.
// The track moves with transforms (no native scroll), and edge clones make the loop seamless.
const CLONES = 2;
const DURATION = 500;

class SSlideshow extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.originals = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
    this.count = this.originals.length;
    if (this.count === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.paused = false;
    this.animating = false;

    if (this.count > 1) this.addClones();
    this.items = Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
    this.offset = this.count > 1 ? CLONES : 0;
    this.index = this.offset;

    requestAnimationFrame(() => this.moveTo(this.index, false));

    this.addEventListener('command', (event) => this.onCommand(event), { signal });
    this.track.addEventListener('transitionend', (event) => this.onTransitionEnd(event), { signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.addEventListener('pointerenter', () => (this.paused = true), { signal });
    this.addEventListener('pointerleave', () => (this.paused = false), { signal });
    this.addEventListener('focusin', () => (this.paused = true), { signal });
    this.addEventListener('focusout', () => (this.paused = false), { signal });
    window.addEventListener('resize', () => this.moveTo(this.index, false), { signal });
    // Neighbouring (peeking) slides are inert, so clicks on them land on the track. Work out
    // which slide is under the pointer and bring it to the centre.
    this.track.addEventListener('click', (event) => this.onTrackClick(event), { signal });
    this.track.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const hit = this.slideIndexAt(event.clientX, event.clientY);
      this.track.style.cursor = hit !== -1 && hit !== this.index ? 'pointer' : '';
    }, { signal });
    this.setupSwipe(signal);

    const interval = Number(this.dataset.interval) || 6000;
    if (this.dataset.autoplay === 'true' && !this.reducedMotion && this.count > 1) {
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden && !this.dragging) this.step(1);
      }, interval);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearInterval(this.timer);
    clearTimeout(this.fallbackTimer);
  }

  addClones() {
    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      return clone;
    };
    for (let i = 0; i < CLONES; i++) {
      this.track.append(makeClone(this.originals[i % this.count]));
      this.track.prepend(makeClone(this.originals[(this.count - 1 - i + this.count * CLONES) % this.count]));
    }
  }

  onCommand(event) {
    if (event.command === '--next') this.step(1);
    else if (event.command === '--prev') this.step(-1);
    else if (event.command === '--go') {
      const target = Number(event.source?.dataset.index);
      if (target) this.goToReal(target - 1);
    }
  }

  slideIndexAt(x, y) {
    return this.items.findIndex((item) => {
      const rect = item.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
  }

  onTrackClick(event) {
    // A click that ends a drag is already cancelled by setupSwipe.
    if (event.defaultPrevented || this.animating || this.count < 2) return;
    const hit = this.slideIndexAt(event.clientX, event.clientY);
    if (hit === -1 || hit === this.index) return;
    event.preventDefault();
    this.track.style.cursor = '';
    this.moveTo(hit, true);
  }

  // Direction follows reading order, so "next" moves toward the inline end in both LTR and RTL.
  step(direction) {
    if (this.animating || this.count < 2) return;
    this.moveTo(this.index + direction, true);
  }

  goToReal(realIndex) {
    if (this.animating) return;
    const target = realIndex + this.offset;
    if (target !== this.index) this.moveTo(target, true);
  }

  translateFor(index) {
    const slide = this.items[index];
    const frame = this.getBoundingClientRect();
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    // Physical geometry measured relative to the track keeps this correct for RTL layouts too.
    const slideCenterInTrack = slideRect.left - trackRect.left + slideRect.width / 2;
    return frame.width / 2 - slideCenterInTrack;
  }

  moveTo(index, animate, dragOffset = 0) {
    this.index = index;
    const animated = animate && !this.reducedMotion;
    this.track.style.transition = animated ? `transform ${DURATION}ms var(--ease-ui)` : 'none';
    this.track.style.transform = `translateX(${this.translateFor(index) + dragOffset}px)`;
    this.setActive();
    if (animated) {
      this.animating = true;
      clearTimeout(this.fallbackTimer);
      // Fallback in case transitionend never fires (e.g. tab hidden mid-animation).
      this.fallbackTimer = setTimeout(() => this.finish(), DURATION + 100);
    } else {
      this.finish();
    }
  }

  onTransitionEnd(event) {
    if (event.target === this.track && event.propertyName === 'transform') this.finish();
  }

  // After landing on a clone, jump invisibly to the matching real slide.
  finish() {
    clearTimeout(this.fallbackTimer);
    this.animating = false;
    if (this.count < 2) return;
    const real = this.realIndex();
    const target = real + this.offset;
    if (target !== this.index) {
      this.index = target;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${this.translateFor(target)}px)`;
      void this.track.offsetWidth;
      this.setActive();
    }
  }

  realIndex() {
    return (((this.index - this.offset) % this.count) + this.count) % this.count;
  }

  setActive() {
    this.items.forEach((item, i) => {
      const isActive = i === this.index;
      item.toggleAttribute('data-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
      item.toggleAttribute('inert', !isActive);
    });
    const current = String(this.realIndex() + 1);
    for (const dot of this.querySelectorAll('[data-dot]')) {
      if (dot.dataset.index === current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  setupSwipe(signal) {
    let startX = 0;
    let deltaX = 0;
    let pointerId = null;

    this.track.addEventListener('pointerdown', (event) => {
      if (this.animating || this.count < 2 || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      deltaX = 0;
      this.dragging = false;
    }, { signal });

    this.track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      deltaX = event.clientX - startX;
      if (!this.dragging && Math.abs(deltaX) > 8) {
        this.dragging = true;
        this.track.setPointerCapture(pointerId);
      }
      if (this.dragging) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${this.translateFor(this.index) + deltaX}px)`;
      }
    }, { signal });

    const end = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!this.dragging) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const threshold = Math.min(80, this.getBoundingClientRect().width * 0.15);
      if (Math.abs(deltaX) > threshold) {
        // Dragging toward the physical left reveals the next slide in LTR, the previous in RTL.
        const forward = deltaX < 0 ? 1 : -1;
        this.moveTo(this.index + (rtl ? -forward : forward), true);
      } else {
        this.moveTo(this.index, true);
      }
      // Swallow the click that follows a drag so slide buttons don't fire.
      const swallow = new AbortController();
      this.addEventListener('click', (clickEvent) => clickEvent.preventDefault(), { capture: true, once: true, signal: swallow.signal });
      setTimeout(() => {
        swallow.abort();
        this.dragging = false;
      }, 0);
    };
    this.track.addEventListener('pointerup', end, { signal });
    this.track.addEventListener('pointercancel', end, { signal });
  }
}

if (!customElements.get('s-slideshow')) customElements.define('s-slideshow', SSlideshow);
